import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MessageCircle, Send } from "lucide-react";
import api from "@/lib/api";

/**
 * "Messages from Admin" — 1:1 chat between the current approved user and the admin team.
 * Uses the DM endpoints in server.py (/api/dm and /api/dm/unread-count).
 */
export default function UserDirectMessages() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const refreshUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/dm/unread-count");
      setUnread(data.count || 0);
    } catch (_) { /* ignore transient */ }
  }, []);

  useEffect(() => {
    refreshUnread();
    const t = setInterval(refreshUnread, 15000);
    return () => clearInterval(t);
  }, [refreshUnread]);

  const loadThread = useCallback(async () => {
    try {
      const { data } = await api.get("/dm");
      setMessages(data.messages || []);
      setUnread(0);
      setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 50);
    } catch (err) {
      toast.error("Cannot load messages");
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadThread();
    // Poll every 4s while the dialog is open — cheap and complements SSE.
    const t = setInterval(loadThread, 4000);
    return () => clearInterval(t);
  }, [open, loadThread]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      const { data } = await api.post("/dm", { text: t });
      setMessages((prev) => [...prev, data]);
      setText("");
      setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 50);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    }
    finally { setSending(false); }
  };

  return (
    <>
      <button
        data-testid="open-admin-dm"
        onClick={() => setOpen(true)}
        className="rounded-2xl border border-slate-800 bg-slate-900 text-white p-6 hover:border-[#00b4ff] hover:bg-slate-950 group text-left relative w-full"
        style={{ transition: "border-color 0.2s ease, background-color 0.2s ease" }}
      >
        <div className="w-10 h-10 rounded-lg bg-slate-800 grid place-items-center text-slate-200 group-hover:bg-[#00b4ff] group-hover:text-white" style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}>
          <MessageCircle size={18} />
        </div>
        <div className="mt-4 font-display font-semibold text-white">Messages from Admin</div>
        <div className="text-sm text-slate-300">Chat direct dengan admin dalam app — reply real-time.</div>
        {unread > 0 && (
          <span
            data-testid="user-dm-unread-badge"
            className="absolute top-4 right-4 min-w-[22px] h-[22px] px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold grid place-items-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden" data-testid="user-dm-dialog">
          <DialogHeader className="p-4 border-b border-slate-100 space-y-0">
            <DialogTitle className="font-display flex items-center gap-2 text-base">
              <MessageCircle size={16} className="text-[#00b4ff]" />
              Messages from Admin
            </DialogTitle>
            <DialogDescription className="font-mono-plex text-[10px] tracking-widest uppercase text-slate-500 mt-1">
              Direct chat · replies visible immediately
            </DialogDescription>
          </DialogHeader>
          <div ref={scrollRef} className="h-[380px] overflow-y-auto p-4 space-y-2 bg-slate-50">
            {messages.length === 0 ? (
              <div className="h-full grid place-items-center text-center">
                <div className="text-xs font-mono-plex text-slate-500">
                  Belum ada mesej. Hantar mesej pertama anda kepada admin di bawah.
                </div>
              </div>
            ) : messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`} data-testid={`user-dm-msg-${m.id}`}>
                <div className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${m.sender === "user" ? "bg-[#00b4ff] text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>
                  {m.text}
                  <div className={`mt-1 text-[10px] font-mono-plex ${m.sender === "user" ? "text-white/70" : "text-slate-400"}`}>
                    {m.sender === "admin" ? "Admin · " : "You · "}
                    {new Date(m.created_at).toLocaleString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={send} className="p-3 border-t border-slate-100 flex gap-2">
            <input
              data-testid="user-dm-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type a message to admin…"
              maxLength={2000}
              className="flex-1 px-4 py-2.5 rounded-full border border-slate-200 focus:outline-none focus:border-[#00b4ff] text-sm"
              style={{ transition: "border-color 0.2s ease" }}
            />
            <button
              type="submit"
              data-testid="user-dm-send"
              disabled={sending || !text.trim()}
              className="inline-flex items-center gap-1.5 bg-[#00b4ff] hover:bg-[#0090cc] disabled:opacity-60 text-white px-4 py-2.5 rounded-full text-sm font-semibold"
              style={{ transition: "background-color 0.2s ease" }}
            >
              <Send size={14} /> {sending ? "…" : "Send"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
